import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const MILESTONES = [30, 100, 365];
const LS_KEY = 'rbr_celebrated_streaks';
const MILESTONE_COPY = {
  30: {
    title: '30 Day Streak',
    tagline: 'A whole month of coming home to yourself. This is a practice now.',
  },
  100: {
    title: '100 Day Streak',
    tagline: 'You built something rare. This is who you are now.',
  },
  365: {
    title: '365 Day Streak',
    tagline: 'A full year of showing up for yourself. Look how far you\u2019ve come.',
  },
};

// Gold + cream confetti palette, on brand.
const CONFETTI_COLORS = ['#e8b93a', '#D6AD60', '#d4a952', '#F7F3EA', '#c69a45'];

const loadCelebrated = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const saveCelebrated = (arr) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(arr));
  } catch {
    /* no-op */
  }
};

/**
 * Fires a full-screen shimmer + confetti moment when the user's streak
 * crosses a milestone (7, 30, 100). Persists which milestones have already
 * been celebrated per-device in localStorage, so it never re-fires on
 * refresh, but a new device or cleared storage will show the highest
 * milestone they've earned exactly once.
 */
const StreakCelebration = () => {
  const { profile } = useAuth();
  const streakCurrent = profile?.streak?.current || 0;
  const [visible, setVisible] = useState(null); // null | 30 | 100 | 365
  const seededRef = useRef(false);

  // Compute the highest milestone the user has earned that hasn't been shown.
  useEffect(() => {
    if (!profile) return;
    const celebrated = loadCelebrated();

    // First-run seed: if a user already has a big streak from before this
    // feature shipped, mark everything they've already passed as
    // "celebrated" so we don't blast them with three overlays in a row on
    // first load. On subsequent hits (a fresh milestone they haven't been
    // celebrated for yet), we fire the highest new one.
    if (!seededRef.current) {
      seededRef.current = true;
      const missing = MILESTONES.filter(
        (m) => streakCurrent >= m && !celebrated.includes(m)
      );
      if (missing.length > 1) {
        // Silently mark all but the biggest as already celebrated on this
        // device — that biggest one is the "wow" moment we surface.
        const toSilentSeed = missing.slice(0, -1);
        const next = [...new Set([...celebrated, ...toSilentSeed])];
        saveCelebrated(next);
        setVisible(missing[missing.length - 1]);
        return;
      }
      if (missing.length === 1) {
        setVisible(missing[0]);
        return;
      }
      return;
    }

    // Live-update path: streak just ticked upward while the app is open.
    const nextMilestone = MILESTONES.find(
      (m) => streakCurrent >= m && !celebrated.includes(m)
    );
    if (nextMilestone) setVisible(nextMilestone);
  }, [streakCurrent, profile]);

  const dismiss = useCallback(() => {
    if (visible == null) return;
    const celebrated = loadCelebrated();
    if (!celebrated.includes(visible)) {
      saveCelebrated([...celebrated, visible]);
    }
    setVisible(null);
  }, [visible]);

  // Precompute confetti pieces — stable per render of the overlay so their
  // animation timing is coherent, not chaotic.
  const confetti = useMemo(() => {
    if (visible == null) return [];
    return Array.from({ length: 60 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.6,
      duration: 2.4 + Math.random() * 1.4,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      size: 8 + Math.random() * 8,
      rotation: Math.random() * 360,
      drift: (Math.random() - 0.5) * 40,
    }));
  }, [visible]);

  // Auto-dismiss after 5.2s if the user doesn't tap.
  useEffect(() => {
    if (visible == null) return;
    const t = setTimeout(dismiss, 5200);
    return () => clearTimeout(t);
  }, [visible, dismiss]);

  if (visible == null) return null;
  const copy = MILESTONE_COPY[visible];

  return (
    <div
      onClick={dismiss}
      data-testid="streak-celebration-overlay"
      role="dialog"
      aria-live="polite"
      className="fixed inset-0 z-[80] flex items-center justify-center cursor-pointer"
      style={{
        background:
          'radial-gradient(circle at 50% 40%, rgba(60,42,18,0.72) 0%, rgba(30,20,8,0.94) 70%)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        animation: 'streakFadeIn 400ms ease-out both',
      }}
    >
      {/* Confetti layer */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {confetti.map((p) => (
          <span
            key={p.id}
            style={{
              position: 'absolute',
              top: '-24px',
              left: `${p.left}%`,
              width: `${p.size}px`,
              height: `${p.size * 0.5}px`,
              backgroundColor: p.color,
              borderRadius: '2px',
              transform: `rotate(${p.rotation}deg)`,
              animation: `streakConfetti ${p.duration}s cubic-bezier(0.35, 0.55, 0.55, 1) ${p.delay}s both`,
              // per-piece horizontal drift via CSS var
              '--drift': `${p.drift}vw`,
              opacity: 0.95,
              boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
            }}
          />
        ))}
      </div>

      {/* Concentric gold shimmer rings */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              width: `${180 + i * 120}px`,
              height: `${180 + i * 120}px`,
              border: '1px solid rgba(232, 185, 58, 0.55)',
              animation: `streakRing 2.4s ease-out ${i * 0.35}s infinite`,
              opacity: 0,
            }}
          />
        ))}
      </div>

      {/* Center card */}
      <div
        className="relative text-center px-8"
        style={{ animation: 'streakPop 700ms cubic-bezier(0.34, 1.56, 0.64, 1) both' }}
      >
        <div
          className="mx-auto mb-5 flex items-center justify-center rounded-full"
          style={{
            width: 96,
            height: 96,
            background:
              'radial-gradient(circle at 30% 30%, #f5d67d 0%, #d4a952 55%, #D6AD60 100%)',
            boxShadow:
              '0 0 60px rgba(232, 185, 58, 0.55), inset 0 0 20px rgba(255, 240, 200, 0.6)',
          }}
        >
          <span
            className="text-4xl"
            style={{
              fontFamily: "'Playfair Display', serif",
              color: '#3d2f1f',
              fontWeight: 700,
            }}
            data-testid="streak-celebration-number"
          >
            {visible}
          </span>
        </div>
        <h2
          className="text-white mb-3"
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: '2rem',
            lineHeight: 1.15,
            textShadow: '0 2px 20px rgba(0,0,0,0.4)',
          }}
          data-testid="streak-celebration-title"
        >
          {copy.title}
        </h2>
        <p
          className="text-[#E8DDCB] text-sm max-w-xs mx-auto leading-relaxed"
          style={{ textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}
        >
          {copy.tagline}
        </p>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            dismiss();
          }}
          className="mt-8 px-8 py-3 rounded-full text-sm font-medium tracking-wide transition-all active:scale-95"
          style={{
            background: 'linear-gradient(135deg, #e8b93a, #D6AD60)',
            color: '#2a1f0f',
            boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'manipulation',
          }}
          data-testid="streak-celebration-dismiss"
        >
          Keep going
        </button>
      </div>

      {/* Inline keyframes — kept local so no global css bloat */}
      <style>{`
        @keyframes streakFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes streakPop {
          0% { opacity: 0; transform: scale(0.65) translateY(20px); }
          60% { opacity: 1; transform: scale(1.05) translateY(0); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes streakConfetti {
          0% {
            transform: translate3d(0, 0, 0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translate3d(var(--drift, 0), 110vh, 0) rotate(720deg);
            opacity: 0.85;
          }
        }
        @keyframes streakRing {
          0% {
            transform: scale(0.4);
            opacity: 0;
          }
          20% { opacity: 0.9; }
          100% {
            transform: scale(1.3);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default StreakCelebration;
